import { FC, useMemo, useState } from "react"
import _ from "lodash"
import moment, { Moment } from "moment"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { toast } from "react-toastify"
import { SIcon } from "@superwall/legacy-ui"

export type Property = {
  key: string
  value: any
}

const ValueText = ({ value }: { value: Property["value"] }) => {
  if (typeof value === "string") {
    const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/
    if (dateRegex.test(value)) {
      return <span>{moment.utc(value).local().format("ddd, MMM D, YYYY h:mm A")}</span>
    }
    if (value !== "") {
      return <span>{value}</span>
    }
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return <span className="font-mono">{value.toString()}</span>
  }

  if (value instanceof Date || value instanceof moment) {
    return <span>{moment.utc(value).local().format("ddd, MMM D, YYYY h:mm A")}</span>
  }

  return <span className="opacity-60">Empty</span>
}

const valueToCopyString = (value: Property["value"]) => {
  // check if the value van be parsed as a date

  if (typeof value === "string") {
    if (value !== "") {
      return value
    }
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return value.toString()
  }

  if (value instanceof Date || value instanceof moment) {
    return moment.utc(value).local().format("ddd, MMM D, YYYY h:mm A")
  }

  return ""
}

const Value = ({ value }: { value: Property["value"] }) => {
  const [copied, setJustCopied] = useState<boolean>(false)

  return (
    <span className="tooltip">
      <CopyToClipboard
        text={valueToCopyString(value)}
        onCopy={() => {
          setJustCopied(true)
          setTimeout(() => {
            setJustCopied(false)
          }, 1250)
        }}
      >
        <span className="cursor-pointer transition-all text-white/50 hover:text-brand-primary relative">
          {copied ? (
            <span className="inline-flex gap-2 items-center relative">
              <div className="absolute">
                <SIcon type="check" size={12}></SIcon>
              </div>
              <span className="pl-[18px]">Copied</span>
            </span>
          ) : (
            <ValueText value={value} />
          )}
        </span>
      </CopyToClipboard>
    </span>
  )
}

export const PropertyList: FC<{
  properties?: Property[]
  keyValues?: any
  overflow?: boolean
  horizontal?: boolean
}> = ({ properties, overflow = true, horizontal = false, keyValues }) => {
  const _properties = useMemo(() => {
    if (properties) {
      return properties
    } else {
      return (Object.entries(keyValues || {}) as [string, string | number | boolean | undefined][])
        .map(([key, value]) => {
          return {
            key,
            value,
          }
        })
        .sort((a, b) => {
          return a.key.localeCompare(b.key)
        })
        .filter((e) => e) as Property[]
    }
    return []
  }, [properties])

  return (
    <div
      className={`divide-y divide-border-light ${
        overflow ? "max-h-[430px] overflow-y-scroll" : " overflow-x-scroll"
      }`}
    >
      {_properties.map(({ key, value }) => {
        return (
          <div
            key={key}
            className={`group ${
              horizontal
                ? "grid grid-cols-[2fr,minmax(0,3fr)] overflow-x-scroll max-content overflow-y-hidden  min-h-[45px]"
                : "flex flex-col"
            } gap-[0.1rem] py-3 px-3 `}
          >
            <div className="text-sm whitespace-nowrap">{key}</div>
            <div
              className={`text-sm whitespace-nowrap md:whitespace-normal ${
                horizontal ? "ml-4" : ""
              }`}
            >
              <Value value={value} />
              <div className="!w-3"></div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
